import React from "react";

import { Helmet } from "react-helmet";
import { Redirect } from "react-router-dom";

import { AuthCard } from "features/Auth/components/AuthCard";
import {
    SIGN_IN_CODE_LENGTH,
    SignInMethod,
    useSignInState,
} from "features/Auth/hooks/useSignInState";
import { Actions, Hint, Root, TextLink } from "features/Auth/styles";
import { formatPhoneNumber } from "shared/lib/phoneNumber";
import {
    Button,
    CodeInput,
    LabeledField,
    NotificationSnackbar,
    PhoneInput,
    TabItem,
    Tabs,
    TextInput,
} from "shared/ui";

const METHOD_TABS: TabItem<SignInMethod>[] = [
    { key: "sms", label: "По SMS" },
    { key: "password", label: "По паролю" },
];

// Без observer: фича не читает observable — и сессия, и вход идут через REST.
export const SignIn = () => {
    const state = useSignInState();

    // Форма входа залогиненному не нужна — уводим в кабинет.
    if (state.isAuthorized) {
        return <Redirect to="/" />;
    }

    return (
        <Root>
            <Helmet title="Вход" />

            <AuthCard subtitle="Вход в личный кабинет">
                {state.step === "code" ? (
                    <>
                        <Hint>
                            Мы отправили код на номер
                            <br />
                            {formatPhoneNumber(state.phone)}
                        </Hint>

                        <CodeInput
                            autoFocus
                            value={state.code}
                            length={SIGN_IN_CODE_LENGTH}
                            disabled={state.submitting}
                            onChange={state.setCode}
                            onComplete={state.submitCode}
                        />

                        <Actions>
                            <Button
                                variant="text"
                                color="neutral"
                                fullWidth
                                disabled={state.submitting}
                                onClick={state.backToPhone}
                            >
                                Ввести другой номер
                            </Button>
                        </Actions>
                    </>
                ) : (
                    <>
                        <Tabs value={state.method} tabs={METHOD_TABS} onChange={state.changeMethod} />

                        <LabeledField label="Телефон" htmlFor="phone">
                            <PhoneInput
                                autoFocus
                                id="phone"
                                value={state.phone}
                                disabled={state.submitting}
                                onChange={state.setPhone}
                                onSubmit={
                                    state.method === "sms" ? state.sendCode : state.submitPassword
                                }
                            />
                        </LabeledField>

                        {state.method === "password" && (
                            <LabeledField label="Пароль">
                                <TextInput
                                    type="password"
                                    value={state.password}
                                    disabled={state.submitting}
                                    onChange={state.setPassword}
                                />
                            </LabeledField>
                        )}

                        <Actions>
                            {state.method === "sms" ? (
                                <Button
                                    fullWidth
                                    disabled={!state.isPhoneComplete || state.submitting}
                                    onClick={state.sendCode}
                                >
                                    Получить код
                                </Button>
                            ) : (
                                <Button
                                    fullWidth
                                    disabled={
                                        !state.isPhoneComplete || !state.password || state.submitting
                                    }
                                    onClick={state.submitPassword}
                                >
                                    Войти
                                </Button>
                            )}

                            <TextLink to="/auth/sign-up">Зарегистрироваться</TextLink>
                        </Actions>
                    </>
                )}
            </AuthCard>

            <NotificationSnackbar {...state.notify.snackbarProps} />
        </Root>
    );
};
